/**
 * Switch Topology View page.
 * Visualizes all interfaces and VLANs for a specific switch.
 * Implements FR-3.3.3: Switch Topology View
 */

import { FC, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Router as RouterIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  DeleteForever as DeleteForeverIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { GraphVisualization, GraphControls } from '@/components/Graph';
import { SwitchVersionHistory } from '@/components/Switch/SwitchVersionHistory';
import { VersionTimeline } from '@/components/Timeline/VersionTimeline';
import { HistoricalVersionModal } from '@/components/HistoricalView/HistoricalVersionModal';
import api from '@/services/api';
import type {
  SwitchQueryResponse,
  SwitchSummary,
  GraphData,
  GraphNode,
  GraphEdge,
  Interface,
  VersionSummary,
} from '@/types/entities';

type SortField = 'name' | 'mode' | 'status';
type SortOrder = 'asc' | 'desc';
type FilterMode = 'all' | 'access' | 'trunk';
type FilterStatus = 'all' | 'up' | 'down';

export const SwitchTopologyView: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hostname: urlHostname } = useParams<{ hostname?: string }>();
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const [selectedSwitch, setSelectedSwitch] = useState<SwitchSummary | null>(null);
  const [switches, setSwitches] = useState<SwitchSummary[]>([]);
  const [switchesLoading, setSwitchesLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [switchData, setSwitchData] = useState<SwitchQueryResponse | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Filter states
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterVlan, setFilterVlan] = useState<number | null>(null);

  // Autocomplete control state
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

  // Version history state
  const [versionHistoryDialogOpen, setVersionHistoryDialogOpen] = useState<boolean>(false);

  // Retire switch state
  const [retireDialogOpen, setRetireDialogOpen] = useState<boolean>(false);
  const [retiring, setRetiring] = useState<boolean>(false);

  // Timeline tab state (COMP-005)
  const [activeTab, setActiveTab] = useState<number>(0);
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const [currentVersionId, setCurrentVersionId] = useState<string>('');
  const [selectedHistoricalVersion, setSelectedHistoricalVersion] = useState<VersionSummary | null>(null);
  const [historicalModalOpen, setHistoricalModalOpen] = useState<boolean>(false);

  /**
   * Load all switches on component mount
   */
  useEffect(() => {
    const loadSwitches = async () => {
      setSwitchesLoading(true);
      try {
        const response = await api.getSwitches();
        setSwitches(response.switches);
      } catch (err) {
        console.error('Failed to load switches:', err);
        // Don't show error for switch list loading - user can still type hostname
      } finally {
        setSwitchesLoading(false);
      }
    };

    loadSwitches();
  }, []);

  /**
   * Handle switch selection from autocomplete
   */
  const handleSwitchChange = useCallback(
    async (_event: React.SyntheticEvent, value: string | SwitchSummary | null) => {
      // If value is a string (typed but not selected), find matching switch or treat as hostname
      let switchToFetch: SwitchSummary | null = null;

      if (typeof value === 'string') {
        // Find switch by hostname if user typed it
        switchToFetch = switches.find((s) => s.hostname === value) || null;
        if (!switchToFetch && value.trim()) {
          // Allow manual hostname entry
          switchToFetch = { hostname: value.trim() };
        }
      } else {
        switchToFetch = value;
      }

      setSelectedSwitch(switchToFetch);

      if (!switchToFetch) {
        setSwitchData(null);
        setError(null);
        // Navigate back to switch selection if URL shows a specific switch
        if (urlHostname) {
          navigate('/switches', { replace: true });
        }
        return;
      }

      // Navigate to switch detail URL if not already there
      if (urlHostname !== switchToFetch.hostname) {
        navigate(`/switch/${switchToFetch.hostname}`, { replace: true });
      }

      // Fetch switch data
      setLoading(true);
      setError(null);
      setSwitchData(null);

      // Reset filters on new selection
      setFilterMode('all');
      setFilterStatus('all');
      setFilterVlan(null);

      try {
        const data = await api.getSwitch(switchToFetch.hostname);
        setSwitchData(data);
        setError(null);
      } catch (err) {
        if (err instanceof Error) {
          setError(`Failed to fetch switch ${switchToFetch.hostname}: ${err.message}`);
        } else {
          setError(
            `Failed to fetch switch ${switchToFetch.hostname}. Please check if the backend is running.`
          );
        }
        setSwitchData(null);
      } finally {
        setLoading(false);
      }
    },
    [switches, urlHostname, navigate]
  );

  /**
   * Auto-focus and open autocomplete when navigating from dashboard
   */
  useEffect(() => {
    if (searchParams.get('focus') === 'true' && switches.length > 0) {
      // Use setTimeout to ensure component is fully mounted and DOM is ready
      setTimeout(() => {
        // Focus the input
        autocompleteInputRef.current?.focus();
        // Open the dropdown
        setAutocompleteOpen(true);
      }, 100);

      // Clean up URL parameter to prevent re-triggering on re-renders
      const params = new URLSearchParams(window.location.search);
      params.delete('focus');
      window.history.replaceState({}, '', `${window.location.pathname}`);
    }
  }, [searchParams, switches]);

  /**
   * Fetch versions when switch is selected (COMP-005)
   */
  const fetchVersions = useCallback(async (hostname: string) => {
    setVersionsLoading(true);
    try {
      const response = await api.getSwitchVersions(hostname);
      setVersions(response.versions);
      setCurrentVersionId(response.currentVersion || '');
    } catch (err) {
      console.error('Failed to fetch versions:', err);
      setVersions([]);
      setCurrentVersionId('');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  /**
   * Load versions when switchData is available
   */
  useEffect(() => {
    if (switchData) {
      fetchVersions(switchData.switch.hostname);
    } else {
      setVersions([]);
      setCurrentVersionId('');
      setActiveTab(0); // Reset to Current tab
    }
  }, [switchData, fetchVersions]);

  /**
   * Auto-load switch from URL parameter
   */
  useEffect(() => {
    if (urlHostname && switches.length > 0 && !selectedSwitch) {
      // Find the switch in the list
      const switchToLoad = switches.find((s) => s.hostname === urlHostname);
      if (switchToLoad) {
        handleSwitchChange(null as any, switchToLoad);
      } else {
        // If not found in list, try loading anyway (manual entry)
        handleSwitchChange(null as any, { hostname: urlHostname });
      }
    }
  }, [urlHostname, switches, selectedSwitch, handleSwitchChange]);

  /**
   * Filter interfaces based on selected filters
   */
  const filteredInterfaces = useMemo(() => {
    if (!switchData) return [];

    return switchData.interfaces.filter((iface) => {
      // Filter by mode
      if (filterMode !== 'all' && iface.mode !== filterMode) return false;

      // Filter by status
      if (filterStatus !== 'all' && iface.status !== filterStatus) return false;

      // Filter by VLAN (check if interface connects to this VLAN)
      if (filterVlan !== null) {
        const hasVlan = switchData.relationships.some(
          (rel) => rel.fromInterface === iface.uniqueId && rel.toVlan === filterVlan
        );
        if (!hasVlan) return false;
      }

      return true;
    });
  }, [switchData, filterMode, filterStatus, filterVlan]);

  /**
   * Calculate statistics
   */
  const calculatedStats = useMemo(() => {
    if (!switchData) return null;

    const activePorts = switchData.interfaces.filter((i) => i.status === 'up').length;
    const downPorts = switchData.interfaces.filter((i) => i.status === 'down').length;
    const accessPorts = switchData.interfaces.filter((i) => i.mode === 'access').length;
    const trunkPorts = switchData.interfaces.filter((i) => i.mode === 'trunk').length;

    return {
      totalPorts: switchData.statistics.port_count,
      activePorts,
      downPorts,
      totalVlans: switchData.statistics.vlan_count,
      accessPorts,
      trunkPorts,
    };
  }, [switchData]);

  /**
   * Transform SwitchQueryResponse to GraphData format with filtering
   */
  const transformToGraphData = useCallback(
    (data: SwitchQueryResponse, filteredIfaces: Interface[], vlanFilter: number | null): GraphData => {
      const nodes: GraphNode[] = [];
      const edges: GraphEdge[] = [];

      // Add Switch node (center)
      nodes.push({
        id: data.switch.hostname,
        label: data.switch.hostname,
        type: 'switch',
        data: data.switch,
      });

      // Add filtered Interface nodes
      filteredIfaces.forEach((iface) => {
        nodes.push({
          id: iface.uniqueId,
          label: iface.name,
          type: 'interface',
          data: iface,
        });
      });

      // Add HAS_INTERFACE edges (switch → interfaces)
      filteredIfaces.forEach((iface) => {
        edges.push({
          id: `${data.switch.hostname}-${iface.uniqueId}`,
          source: data.switch.hostname,
          target: iface.uniqueId,
          type: 'HAS_INTERFACE',
          label: '',
        });
      });

      // Collect VLANs connected to filtered interfaces
      const connectedVlanIds = new Set<number>();

      if (vlanFilter !== null) {
        // If VLAN filter is active, only include that specific VLAN
        connectedVlanIds.add(vlanFilter);
      } else {
        // If no VLAN filter, include all VLANs connected to filtered interfaces
        data.relationships.forEach((rel) => {
          if (filteredIfaces.some((i) => i.uniqueId === rel.fromInterface)) {
            connectedVlanIds.add(rel.toVlan);
          }
        });
      }

      // Add VLAN nodes
      data.vlans.forEach((vlan) => {
        if (connectedVlanIds.has(vlan.vlanID)) {
          nodes.push({
            id: `vlan-${vlan.vlanID}`,
            label: `VLAN ${vlan.vlanID}`,
            type: 'vlan',
            data: vlan,
          });
        }
      });

      // Add Interface → VLAN edges
      data.relationships.forEach((rel) => {
        if (!filteredIfaces.some((i) => i.uniqueId === rel.fromInterface)) return;
        if (!connectedVlanIds.has(rel.toVlan)) return;

        edges.push({
          id: `${rel.fromInterface}-vlan-${rel.toVlan}`,
          source: rel.fromInterface,
          target: `vlan-${rel.toVlan}`,
          type: rel.type,
          label:
            rel.type === 'TRUNKS_VLAN' && rel.properties.native
              ? 'native'
              : rel.type === 'TRUNKS_VLAN'
              ? 'trunk'
              : 'member',
          data: {
            native: rel.properties.native,
          },
        });
      });

      return { nodes, edges };
    },
    []
  );


  /**
   * Export data as CSV
   */
  const handleExportCSV = useCallback(() => {
    if (!switchData) return;

    // Build CSV content with switch info header
    const switchInfo = [
      `Switch: ${switchData.switch.hostname}`,
      `Vendor: ${switchData.switch.vendor || 'N/A'}`,
      `Model: ${switchData.switch.model || 'N/A'}`,
      `Total Ports: ${calculatedStats?.totalPorts || 0}`,
      `Total VLANs: ${calculatedStats?.totalVlans || 0}`,
      '',
    ];

    const headers = [
      'Interface Name',
      'Unique ID',
      'Mode',
      'Status',
      'Description',
      'Connected VLANs',
    ];
    const rows = switchData.interfaces.map((iface) => {
      const connectedVlans = switchData.relationships
        .filter((rel) => rel.fromInterface === iface.uniqueId)
        .map((rel) => rel.toVlan)
        .sort((a, b) => a - b)
        .join(', ');

      return [
        iface.name,
        iface.uniqueId,
        iface.mode || 'unknown',
        iface.status || 'unknown',
        iface.description || '',
        connectedVlans || 'None',
      ];
    });

    const csvContent = [
      ...switchInfo,
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `switch-${switchData.switch.hostname}-topology.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [switchData, calculatedStats]);

  /**
   * Export data as JSON
   */
  const handleExportJSON = useCallback(() => {
    if (!switchData) return;

    const jsonContent = JSON.stringify(switchData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `switch-${switchData.switch.hostname}-data.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [switchData]);

  /**
   * Get connected VLANs for an interface
   */
  const getConnectedVlans = useCallback(
    (interfaceId: string): number[] => {
      if (!switchData) return [];
      return switchData.relationships
        .filter((rel) => rel.fromInterface === interfaceId)
        .map((rel) => rel.toVlan)
        .sort((a, b) => a - b);
    },
    [switchData]
  );

  /**
   * Get interfaces connected to a VLAN
   */
  const getVlanInterfaces = useCallback(
    (vlanId: number): Interface[] => {
      if (!switchData) return [];
      const interfaceIds = switchData.relationships
        .filter((rel) => rel.toVlan === vlanId)
        .map((rel) => rel.fromInterface);
      return switchData.interfaces.filter((iface) =>
        interfaceIds.includes(iface.uniqueId)
      );
    },
    [switchData]
  );

  /**
   * Navigate to VLAN detail view
   */
  const handleViewVlan = useCallback(
    (vlanId: number) => {
      navigate(`/vlans?id=${vlanId}`);
    },
    [navigate]
  );

  /**
   * Memoized graph data to prevent unnecessary re-renders
   */
  const graphData = useMemo(() => {
    if (!switchData) return { nodes: [], edges: [] };
    return transformToGraphData(switchData, filteredInterfaces, filterVlan);
  }, [switchData, filteredInterfaces, filterVlan, transformToGraphData]);

  /**
   * Handle table sort
   */
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder]
  );

  /**
   * Sort interfaces
   */
  const sortedInterfaces = useCallback(
    (interfaces: Interface[]): Interface[] => {
      return [...interfaces].sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (sortField) {
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'mode':
            aValue = a.mode || '';
            bValue = b.mode || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    [sortField, sortOrder]
  );

  /**
   * Navigate to interface detail view
   */
  const handleViewInterface = useCallback(
    (interfaceId: string) => {
      navigate(`/interfaces?id=${encodeURIComponent(interfaceId)}`);
    },
    [navigate]
  );

  /**
   * Reset all filters
   */
  const handleResetFilters = useCallback(() => {
    setFilterMode('all');
    setFilterStatus('all');
    setFilterVlan(null);
  }, []);

  /**
   * Handle version history dialog open/close
   */
  const handleOpenVersionHistory = useCallback(() => {
    setVersionHistoryDialogOpen(true);
  }, []);

  const handleCloseVersionHistory = useCallback(() => {
    setVersionHistoryDialogOpen(false);
  }, []);

  /**
   * Handle successful version deletion - refresh switch data
   */
  const handleVersionDeleted = useCallback(() => {
    if (selectedSwitch) {
      // Refresh switch data after deletion
      setTimeout(() => {
        handleSwitchChange({} as React.SyntheticEvent, selectedSwitch);
      }, 500);
    }
  }, [selectedSwitch, handleSwitchChange]);

  /**
   * Retire switch handlers
   */
  const handleOpenRetireDialog = useCallback(() => {
    setRetireDialogOpen(true);
  }, []);

  const handleCloseRetireDialog = useCallback(() => {
    setRetireDialogOpen(false);
  }, []);

  const handleConfirmRetire = useCallback(async () => {
    if (!selectedSwitch) return;

    try {
      setRetiring(true);
      await api.retireSwitch(selectedSwitch.hostname);

      // Close dialog
      setRetireDialogOpen(false);

      // Navigate back to the list after successful retirement
      navigate('/switches');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retire switch');
      setRetireDialogOpen(false);
    } finally {
      setRetiring(false);
    }
  }, [selectedSwitch, navigate]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Switch Topology View
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Search for a switch to view its complete topology, interfaces, and VLANs.
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Autocomplete
            options={switches}
            value={selectedSwitch}
            onChange={handleSwitchChange}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.vendor && option.model
                ? `${option.hostname} (${option.vendor} ${option.model})`
                : option.hostname;
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body1">{option.hostname}</Typography>
                    {option.vendor && option.model && (
                      <Typography variant="caption" color="text.secondary">
                        {option.vendor} {option.model}
                        {option.managementIp && ` - ${option.managementIp}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={autocompleteInputRef}
                label="Select Switch"
                placeholder="Type to search or select a switch..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {switchesLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={switchesLoading}
            disabled={loading}
            freeSolo
            sx={{ flexGrow: 1 }}
          />
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Switch Data */}
      {switchData && (
        <>
          {/* Tabs for Current vs Timeline view (COMP-005) */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ pb: 0 }}>
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab label="Current Configuration" />
                <Tab label="Version Timeline" />
              </Tabs>
            </CardContent>
          </Card>

          {/* Current Configuration Tab */}
          {activeTab === 0 && (
            <Box>
              {/* Switch Properties and Statistics */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RouterIcon color="primary" />
                  <Typography variant="h6">Switch Properties</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Export as CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export as JSON">
                    <IconButton size="small" onClick={handleExportJSON}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Manage Version History">
                    <IconButton
                      size="small"
                      onClick={handleOpenVersionHistory}
                      color="primary"
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Retire Switch (Permanently Delete)">
                    <IconButton
                      size="small"
                      onClick={handleOpenRetireDialog}
                      color="error"
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Hostname
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {switchData.switch.hostname}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Management IP
                  </Typography>
                  <Typography variant="body1">{switchData.switch.managementIp || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Vendor
                  </Typography>
                  <Typography variant="body1">{switchData.switch.vendor || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Model
                  </Typography>
                  <Typography variant="body1">{switchData.switch.model || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Statistics */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AssessmentIcon color="primary" fontSize="small" />
                <Typography variant="h6" fontSize="1rem">
                  Statistics
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="primary">
                      {calculatedStats?.totalPorts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Ports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main">
                      {calculatedStats?.activePorts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Ports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="error.main">
                      {calculatedStats?.downPorts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Down Ports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="warning.main">
                      {calculatedStats?.totalVlans || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total VLANs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main">
                      {calculatedStats?.accessPorts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Access Ports
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="info.main">
                      {calculatedStats?.trunkPorts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trunk Ports
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Filter Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filter Controls
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Interface Type</InputLabel>
                    <Select
                      value={filterMode}
                      label="Interface Type"
                      onChange={(e) => setFilterMode(e.target.value as FilterMode)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="access">Access Only</MenuItem>
                      <MenuItem value="trunk">Trunk Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="up">Up Only</MenuItem>
                      <MenuItem value="down">Down Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>VLAN</InputLabel>
                    <Select
                      value={filterVlan === null ? '' : filterVlan}
                      label="VLAN"
                      onChange={(e) => setFilterVlan(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <MenuItem value="">All VLANs</MenuItem>
                      {switchData.vlans
                        .sort((a, b) => a.vlanID - b.vlanID)
                        .map((vlan) => (
                          <MenuItem key={vlan.vlanID} value={vlan.vlanID}>
                            VLAN {vlan.vlanID} - {vlan.name || 'Unnamed'}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button variant="outlined" fullWidth onClick={handleResetFilters}>
                    Reset Filters
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredInterfaces.length} of {switchData.interfaces.length} interfaces
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Graph Visualization */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Topology Graph
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Interactive graph showing switch topology. Filters affect which interfaces and VLANs are
                displayed.
              </Typography>
              {filteredInterfaces.length === 0 ? (
                <Alert severity="info">No interfaces match the selected filters.</Alert>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <GraphVisualization
                    ref={graphContainerRef}
                    data={graphData}
                    height="600px"
                    layout="breadthfirst"
                    centerNodeId={switchData.switch.hostname}
                  />
                  <GraphControls containerRef={graphContainerRef} />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* VLAN List Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                VLAN Configuration ({switchData.vlans.length} VLANs)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                All VLANs configured on this switch.
              </Typography>
              {switchData.vlans.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No VLANs configured on this switch.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>VLAN ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Interfaces</TableCell>
                        <TableCell>First Seen</TableCell>
                        <TableCell>Last Seen</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {switchData.vlans
                        .sort((a, b) => a.vlanID - b.vlanID)
                        .map((vlan) => {
                          const vlanInterfaces = getVlanInterfaces(vlan.vlanID);
                          const hasInterfaces = vlanInterfaces.length > 0;

                          return (
                            <TableRow key={vlan.vlanID}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {vlan.vlanID}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {vlan.name || (
                                    <span style={{ fontStyle: 'italic', color: '#999' }}>
                                      Unnamed
                                    </span>
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={vlan.status || 'active'}
                                  size="small"
                                  color={vlan.status === 'active' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={vlanInterfaces.length}
                                    size="small"
                                    color={hasInterfaces ? 'primary' : 'default'}
                                  />
                                  {hasInterfaces && (
                                    <Typography variant="caption" color="text.secondary">
                                      interface{vlanInterfaces.length !== 1 ? 's' : ''}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {vlan.firstSeen
                                    ? new Date(vlan.firstSeen).toLocaleDateString()
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {vlan.lastSeen
                                    ? new Date(vlan.lastSeen).toLocaleDateString()
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="View VLAN Detail">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewVlan(vlan.vlanID)}
                                    color="primary"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Interface List Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interface List ({filteredInterfaces.length} interfaces)
              </Typography>
              {filteredInterfaces.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No interfaces match the selected filters.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'name'}
                            direction={sortField === 'name' ? sortOrder : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Interface Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'mode'}
                            direction={sortField === 'mode' ? sortOrder : 'asc'}
                            onClick={() => handleSort('mode')}
                          >
                            Mode
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'status'}
                            direction={sortField === 'status' ? sortOrder : 'asc'}
                            onClick={() => handleSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Connected VLANs</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedInterfaces(filteredInterfaces).map((iface) => {
                        const connectedVlans = getConnectedVlans(iface.uniqueId);
                        const isDown = iface.status === 'down';

                        return (
                          <TableRow
                            key={iface.uniqueId}
                            sx={{
                              backgroundColor: isDown ? 'rgba(211, 47, 47, 0.08)' : 'inherit',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {iface.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {iface.uniqueId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={iface.mode || 'unknown'}
                                size="small"
                                color={
                                  iface.mode === 'access'
                                    ? 'success'
                                    : iface.mode === 'trunk'
                                    ? 'info'
                                    : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {iface.status === 'up' ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : (
                                  <CancelIcon fontSize="small" color="error" />
                                )}
                                <Chip
                                  label={iface.status || 'unknown'}
                                  size="small"
                                  color={iface.status === 'up' ? 'success' : 'error'}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {iface.description || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {connectedVlans.length > 0 ? connectedVlans.join(', ') : 'None'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Interface Detail">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInterface(iface.uniqueId)}
                                  color="primary"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          </Box>
          )}

          {/* Timeline Tab (COMP-005) */}
          {activeTab === 1 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {versionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading version history...</Typography>
                  </Box>
                ) : versions.length > 0 ? (
                  <VersionTimeline
                    versions={versions}
                    currentVersionId={currentVersionId}
                    hostname={selectedSwitch?.hostname || ''}
                    onVersionSelect={(version) => {
                      setSelectedHistoricalVersion(version);
                      setHistoricalModalOpen(true);
                    }}
                  />
                ) : (
                  <Alert severity="info">
                    No version history available for this switch.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Version History Dialog */}
      <Dialog
        open={versionHistoryDialogOpen}
        onClose={handleCloseVersionHistory}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">Manage Switch Version History</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSwitch && (
            <SwitchVersionHistory
              hostname={selectedSwitch.hostname}
              onDeleted={handleVersionDeleted}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVersionHistory}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Retire Switch Confirmation Dialog */}
      <Dialog
        open={retireDialogOpen}
        onClose={handleCloseRetireDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <WarningIcon />
            <Typography variant="h6">Retire Switch</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to <strong>permanently retire</strong> switch{' '}
            <strong>{selectedSwitch?.hostname}</strong>.
          </DialogContentText>

          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ⚠️ WARNING: This action is irreversible!
            </Typography>
            <Typography variant="body2">
              This will permanently delete:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>ALL versions of this switch (including current)</li>
              <li>ALL associated interface configurations</li>
              <li>ALL relationships and historical data</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Use this only for decommissioned switches.</strong>
            </Typography>
          </Alert>

          <DialogContentText>
            To manage individual versions instead, use the{' '}
            <strong>Manage Version History</strong> button.
          </DialogContentText>

          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Are you absolutely sure you want to retire this switch?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRetireDialog} disabled={retiring}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRetire}
            color="error"
            variant="contained"
            disabled={retiring}
            startIcon={retiring ? <CircularProgress size={20} /> : <DeleteForeverIcon />}
          >
            {retiring ? 'Retiring...' : 'Yes, Retire Switch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historical Version Modal (COMP-005) */}
      {selectedHistoricalVersion && selectedSwitch && (
        <HistoricalVersionModal
          open={historicalModalOpen}
          onClose={() => setHistoricalModalOpen(false)}
          hostname={selectedSwitch.hostname}
          version={selectedHistoricalVersion}
          currentVersionId={currentVersionId}
        />
      )}
    </Box>
  );
};
